import type { PlacementRow } from '@/lib/platform-matrix/placement-matrix';

/**
 * The honest crop signal for the Composer preview.
 *
 * Publishing crops an image ONLY when the user sets an explicit per-platform
 * aspect override (then Mora center-crops deterministically). With NO override,
 * Mora ships the raw bytes and the PLATFORM crops to fit its accept window. So
 * the warning fires only in that no-override case, and only when the source
 * actually falls outside the platform's window — never against a "recommended"
 * ratio the platform would have displayed fine.
 *
 * The metric is the single-axis EDGE-crop fraction (how much of the long edge
 * the platform trims) — what a human perceives as "how much got cut" — not the
 * area loss, which explodes to 40–60% on routine conversions.
 */

export type CropTier = 'minor' | 'major';

export interface CropSignal {
  /** Edge-crop fraction 0..1. */
  fraction: number;
  /** Rounded percent for display. */
  percent: number;
  /** Which edges the platform trims. */
  axis: 'sides' | 'top & bottom';
  tier: CropTier;
}

/** Below this the crop is negligible — no warning. */
export const MINOR_CROP_THRESHOLD = 0.08;
/** At/above this the crop is severe — stronger emphasis. */
export const MAJOR_CROP_THRESHOLD = 0.25;

/** Parse a persisted aspect string ('16:9', '1.78:1', or '1.78') to W/H. */
export function parseAspectValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const v = value.trim();
  if (v.includes(':')) {
    const [w, h] = v.split(':').map(Number);
    return Number.isFinite(w) && Number.isFinite(h) && h !== 0 ? w / h : null;
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Edge-crop fraction when `sourceRatio` is clamped into `[acceptMin, acceptMax]`
 * (what the platform does to fit). Returns 0 when the source is already inside
 * the window. The axis is the pair of edges trimmed.
 */
export function edgeCropFraction(
  sourceRatio: number,
  acceptMin: number,
  acceptMax: number,
): { fraction: number; axis: 'sides' | 'top & bottom' } {
  const target = Math.min(Math.max(sourceRatio, acceptMin), acceptMax);
  if (target === sourceRatio) return { fraction: 0, axis: 'sides' };
  // Source wider than the window → the platform trims the SIDES; taller → top & bottom.
  if (sourceRatio > target) return { fraction: 1 - target / sourceRatio, axis: 'sides' };
  return { fraction: 1 - sourceRatio / target, axis: 'top & bottom' };
}

/**
 * The crop signal to surface, or null when nothing should warn:
 * - no source ratio yet (probe pending / unknown) → null
 * - the user already chose an override (Mora crops to their pick) → null
 * - the crop is below the minor threshold → null
 */
export function cropSignalFor(
  sourceRatio: number | null,
  row: Pick<PlacementRow, 'acceptMin' | 'acceptMax'> | undefined,
  hasOverride: boolean,
): CropSignal | null {
  if (sourceRatio == null || !row || hasOverride) return null;
  const { fraction, axis } = edgeCropFraction(sourceRatio, row.acceptMin, row.acceptMax);
  if (fraction < MINOR_CROP_THRESHOLD) return null;
  return {
    fraction,
    percent: Math.round(fraction * 100),
    axis,
    tier: fraction >= MAJOR_CROP_THRESHOLD ? 'major' : 'minor',
  };
}
